import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Card,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
  Box,
  TextField
} from "@mui/material";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useParams, useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { List } from "react-window";
import * as yup from "yup";
import Appbar from "../../components/Appbar";
import Body from "../../components/Body";
import Drawer from "../../components/Drawer";
import { EditsubadminApi } from "../../store/sub-admin/editSubadmin";
import { addsubadminApi } from "../../store/sub-admin/addSubadmin";
import { getParticularsubadminAPI } from "../../store/sub-admin/getSubadminById";
import { DevTool } from "@hookform/devtools";
import { useTheme } from '@mui/material/styles';
import { getRoleApi } from "../../store/role-permission/getRolesList";

const schema = yup
  .object({
    username: yup
      .string()
      .required("Please provide a username")
      .trim()
      .matches(/^\S*$/, "Username cannot contain spaces"),
    role_id: yup
      .string()
      .required("Please select a role")
      .trim(),
    password: yup
      .string()
      .optional("Password is Required.")
      .min(
        8,
        "password must contain 8 or more characters with at least one of each: uppercase, lowercase, number and special"
      )
      .matches(/[a-z]/, "password must contain at least 1 lower case letter")
      .matches(/[A-Z]/, "password must contain at least 1 upper case letter")
      .matches(/[0-9]/, "password must contain at least 1 number")
      .matches(/[$&/+,:;=?@#|'<>.^*()%!-]/, "password must contain at least 1 special character"),
    is_active: yup.string().optional("Please provide a status ").trim(),
  })
  .required();
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const SEARCH_FIELD_HEIGHT = 64;
const VIRTUAL_MENU_OVERSCAN = 6;
const VIRTUAL_MENU_HEIGHT = ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP;

const PermissionMenuRow = ({ index, items, style }) => (
  <Box style={style}>
    {items[index]}
  </Box>
);

const VirtualizedMenuList = React.forwardRef(function VirtualizedMenuList(props, ref) {
  const { children, hasMore, loading, onLoadMore, searchValue, onSearchChange, sx, ...other } = props;
  const items = React.Children.toArray(children);
  const menuHeight = Math.min(VIRTUAL_MENU_HEIGHT, items.length * ITEM_HEIGHT + ITEM_PADDING_TOP);

  const handleRowsRendered = ({ stopIndex }) => {
    if (hasMore && !loading && stopIndex >= items.length - VIRTUAL_MENU_OVERSCAN) {
      onLoadMore?.();
    }
  };

  return (
    <Box
      ref={ref}
      role="listbox"
      {...other}
      sx={{
        ...sx,
        height: menuHeight + SEARCH_FIELD_HEIGHT,
        maxHeight: VIRTUAL_MENU_HEIGHT + SEARCH_FIELD_HEIGHT,
        p: 0,
      }}
    >
      <Box sx={{ height: SEARCH_FIELD_HEIGHT, p: 1, pb: 0 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Search roles"
          value={searchValue}
          onChange={(event) => onSearchChange?.(event.target.value)}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key !== "Escape") {
              event.stopPropagation();
            }
          }}
        />
      </Box>
      <List
        defaultHeight={menuHeight}
        onRowsRendered={handleRowsRendered}
        overscanCount={VIRTUAL_MENU_OVERSCAN}
        rowComponent={PermissionMenuRow}
        rowCount={items.length}
        rowHeight={ITEM_HEIGHT}
        rowProps={{ items }}
        style={{ height: menuHeight, width: "100%" }}
      />
    </Box>
  );
});

const getPermissionMenuProps = ({ hasMore, loading, onLoadMore, searchValue, onSearchChange }) => ({
  PaperProps: {
    style: {
      maxHeight: VIRTUAL_MENU_HEIGHT + SEARCH_FIELD_HEIGHT,
      width: 250,
      overflow: "hidden",
    },
  },
  MenuListProps: {
    component: VirtualizedMenuList,
    disablePadding: true,
    hasMore,
    loading,
    onLoadMore,
    searchValue,
    onSearchChange,
  },
});

function getStyles(name, personName, theme) {
  return {
    fontWeight:
      personName.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

const getSubadminRoleId = (subadmin) => (
  subadmin?.role_id
  || subadmin?.role?.id
  || (!Array.isArray(subadmin?.roles) ? subadmin?.roles?.id : "")
  || (Array.isArray(subadmin?.roles) ? subadmin.roles[0]?.id : "")
  || ""
);

export default function EditSubadmin() {
  const theme = useTheme();
  const categoryDetails = useSelector((state) => state?.getparticularDetailssubadmin);
  const { result = {} } = useSelector((state) => state?.getparticularDetailssubadmin || {});

  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    control
  } = useForm({ resolver: yupResolver(schema) });
  const params = useParams();
  const navigate = useNavigate();
  const categoryAdd = useSelector((state) => state?.addsubadmin);
  const categoryEdit = useSelector((state) => state?.Editsubadmin);
  const {
    result: modulePermissionList = [],
    loading: modulePermissionLoading = false,
    next: modulePermissionNext,
    page: modulePermissionPage = 1,
  } = useSelector((state) => state?.getRoleList || {});
  useEffect(() => {
    if (params.id) {
      dispatch(getParticularsubadminAPI(params.id));
    }
  }, [dispatch, params.id]);

  const [modulePermissionSearch, setModulePermissionSearch] = useState("");
  const [debouncedModulePermissionSearch, setDebouncedModulePermissionSearch] = useState("");
  const hasMoreModulePermissions = Boolean(modulePermissionNext);
  const permissionLabelsById = React.useMemo(() => {
    const permissions = [
      ...(result?.role ? [result.role] : []),
      ...(result?.roles && !Array.isArray(result.roles) ? [result.roles] : []),
      ...(Array.isArray(result?.roles) ? result.roles : []),
      ...(Array.isArray(modulePermissionList) ? modulePermissionList : []),
    ];

    return permissions.reduce((labels, permission) => {
      labels[permission.id] = permission.name || permission.role_name;
      return labels;
    }, {});
  }, [modulePermissionList, result?.role, result?.roles]);
  const modulePermissionMenuProps = React.useMemo(() => getPermissionMenuProps({
    hasMore: hasMoreModulePermissions,
    loading: modulePermissionLoading,
    onLoadMore: () => {
      if (hasMoreModulePermissions && !modulePermissionLoading) {
        dispatch(getRoleApi({
          page: modulePermissionPage + 1,
          search: debouncedModulePermissionSearch,
        }));
      }
    },
    searchValue: modulePermissionSearch,
    onSearchChange: setModulePermissionSearch,
  }), [
    dispatch,
    debouncedModulePermissionSearch,
    hasMoreModulePermissions,
    modulePermissionLoading,
    modulePermissionPage,
    modulePermissionSearch,
  ]);
  const handleUpdateForm = async (value) => {
    var newObj;
    newObj = { ...value };
    // return false;
    if (params.id) {
      newObj = { ...value, id: params.id };
      await dispatch(EditsubadminApi(newObj));
      if (!categoryEdit?.loading) {
        navigate("/subadmin-management");
        //console.log("edit");
      }
    } else {
      await dispatch(addsubadminApi(newObj));
      if (!categoryAdd?.loading) {
        navigate("/subadmin-management");
        //console.log("true");
      }
    }
  };

  useEffect(() => {
    let defalutValues = {};
    if (!categoryDetails.loading && params.id) {
      defalutValues["username"] = categoryDetails.result?.username;
      defalutValues["is_active"] = `${categoryDetails.result?.is_active}`;
      defalutValues["role_id"] = getSubadminRoleId(categoryDetails.result);
      reset(defalutValues);
    }
  }, [categoryDetails, reset, params.id]);

  useEffect(() => {
    dispatch(getRoleApi({
      page: 1,
      search: debouncedModulePermissionSearch,
    }))
  }, [debouncedModulePermissionSearch, dispatch]);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedModulePermissionSearch(modulePermissionSearch.trim());
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [modulePermissionSearch]);
  return (
    <>
      <Appbar />
      <Drawer />
      <Body>
        <form onSubmit={handleSubmit(handleUpdateForm)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Card>
                <Typography>Subadmin Details</Typography>
                <Stack spacing={2} my={2}>
                  <Divider />

                  <Stack direction="row" spacing={2}>
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel htmlFor="outlined-adornment-username" shrink>
                        Username
                      </InputLabel>
                      <OutlinedInput
                        {...register("username")}
                        label="Username"
                        notched
                        id="outlined-adornment-username"
                        error={errors?.username?.message}
                        defaultValue={watch('username', "")}
                      />
                      <FormHelperText error>
                        {errors?.username?.message}
                      </FormHelperText>
                    </FormControl>
                    {!params.id && (<>
                      <FormControl variant="outlined" fullWidth>
                        <InputLabel htmlFor="outlined-adornment-password" shrink>
                          {!params.id ? "Password" : "New Password"}
                        </InputLabel>
                        <OutlinedInput
                          {...register("password")}
                          label={!params.id ? "Password" : "New Password"}
                          notched
                          id="outlined-adornment-password"
                          error={errors?.password?.message}
                          value={watch('password', "")}
                        />
                        <FormHelperText error>
                          {errors?.password?.message}
                        </FormHelperText>
                      </FormControl>
                    </>)}
                    {params.id && (<FormControl variant="outlined" fullWidth>
                      <InputLabel htmlFor="outlined-adornment-is_active" shrink>
                        Status
                      </InputLabel>
                      <Select
                        {...register("is_active")}
                        label="Status"
                        notched
                        id="outlined-adornment-is_active"
                        error={!!errors?.is_active?.message}
                        value={watch('is_active', "")}
                      >
                        <MenuItem value={"true"}>Active</MenuItem>
                        <MenuItem value={"false"}>InActive</MenuItem>
                      </Select>

                      <FormHelperText error>
                        {errors?.is_active?.message}
                      </FormHelperText>
                    </FormControl>)}
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel id="module-permission-label" htmlFor="role_id" shrink>
                        Role
                      </InputLabel>
                      <Controller
                        name="role_id"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId="module-permission-label"
                            label="Role"
                            value={field.value || ""}
                            disabled={modulePermissionLoading && modulePermissionList.length === 0}
                            onChange={(e) => {
                              field.onChange(e.target.value);
                            }}
                            input={<OutlinedInput id="role_id" label="Role" notched />}
                            renderValue={(selected) => permissionLabelsById[selected] || selected}
                            MenuProps={modulePermissionMenuProps}
                          >
                            {!modulePermissionLoading && modulePermissionList.length === 0 && (
                              <MenuItem disabled>
                                No roles found
                              </MenuItem>
                            )}
                            {modulePermissionLoading && modulePermissionList.length === 0 && (
                              <MenuItem disabled>
                                Loading roles...
                              </MenuItem>
                            )}
                            {modulePermissionList.map((obj) => (
                              <MenuItem
                                key={obj.id}
                                value={obj.id}
                                style={getStyles(obj.id, [field.value].filter(Boolean), theme)}
                              >
                                {obj.name || obj.role_name}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      <FormHelperText error>
                        {errors?.role_id?.message}
                      </FormHelperText>
                    </FormControl>
                  </Stack>
                </Stack>
              </Card>
              <Button
                type="button"
                sx={{ float: "right", mt: 2, ml: 2 }}
                variant="contained"
                onClick={() => navigate("/subadmin-management")}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                sx={{ float: "right", mt: 2 }}
                variant="contained"
              >
                {params.id ? "Update" : "Create"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Body>
      <DevTool control={control} />

    </>
  );
}
